import {
  type CalendarDate,
  endOfMonth,
  getLocalTimeZone,
  parseDate,
  startOfMonth,
  today,
} from '@internationalized/date';
import { selectEvenlySpacedItems } from '@orn/shared-web/components/Chart';

import { type CalendarDateRange } from '../DateRangePicker';

export enum TransactionGroupType {
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

/**
 * Calculate the difference in days between two CalendarDate objects.
 * Uses epoch days for accurate calculation across different calendar systems.
 */
function daysBetween(start: CalendarDate, end: CalendarDate): number {
  // Convert both dates to UTC Date objects at midnight, then calculate epoch days
  const startEpochDays = Math.floor(start.toDate('UTC').getTime() / (1000 * 60 * 60 * 24));
  const endEpochDays = Math.floor(end.toDate('UTC').getTime() / (1000 * 60 * 60 * 24));
  return endEpochDays - startEpochDays;
}

/**
 * Determine how to group transactions based on the date range
 * - Less than 60 days: group by day
 * - Less than 2 years (730 days): group by month
 * - 2 years or more: group by year
 */
export function getTransactionGroupType(dateRange?: CalendarDateRange): TransactionGroupType {
  if (!dateRange) {
    return TransactionGroupType.MONTH;
  }

  const daysDiff = daysBetween(dateRange.start, dateRange.end);

  if (daysDiff < 60) {
    return TransactionGroupType.DAY;
  } else if (daysDiff < 730) {
    return TransactionGroupType.MONTH;
  }
  return TransactionGroupType.YEAR;
}

export interface IBarSizing {
  width: string;
  widthPx?: number;
  barCategoryGap: number;
  barSize?: number;
  shouldScroll: boolean;
}

/**
 * Calculate responsive sizing for bar charts
 * Determines bar size, gap, and whether scrolling is needed
 */
export function calculateBarSizing(params: {
  numBars: number;
  containerWidth: number;
  minBarWidth?: number;
  maxGap?: number;
  minGap?: number;
  yAxisWidth?: number;
  padding?: number;
  margins?: number;
}): IBarSizing {
  const {
    numBars,
    containerWidth,
    minBarWidth = 20,
    maxGap = 12,
    minGap = 4,
    yAxisWidth = 80,
    padding = 40,
    margins = 10,
  } = params;

  if (numBars === 0) {
    return {
      width: '100%',
      widthPx: undefined,
      barCategoryGap: maxGap,
      barSize: undefined,
      shouldScroll: false,
    };
  }

  const availableWidth = containerWidth - yAxisWidth - padding - margins;
  const minRequiredWidth = numBars * minBarWidth + (numBars - 1) * minGap;

  if (availableWidth < minRequiredWidth) {
    const neededWidth = minRequiredWidth + yAxisWidth + padding;
    return {
      width: '100%',
      widthPx: neededWidth,
      barCategoryGap: minGap,
      barSize: minBarWidth,
      shouldScroll: true,
    };
  }

  const extraSpace = availableWidth - minRequiredWidth;
  const maxGapGrowth = maxGap - minGap;
  const growthRatio = numBars + 4 * (numBars - 1);
  const growthToMaxGap = maxGapGrowth / 4;
  const spaceNeededForMaxGap = growthToMaxGap * numBars + maxGapGrowth * (numBars - 1);

  let barSize: number;
  let gap: number;

  if (extraSpace <= spaceNeededForMaxGap) {
    const growthPerBar = extraSpace / growthRatio;
    barSize = minBarWidth + growthPerBar;
    gap = minGap + growthPerBar * 4;
  } else {
    const remainingSpace = extraSpace - spaceNeededForMaxGap;
    const additionalBarGrowth = remainingSpace / numBars;
    barSize = minBarWidth + growthToMaxGap + additionalBarGrowth;
    gap = maxGap;
  }

  return {
    width: '100%',
    widthPx: undefined,
    barCategoryGap: Math.round(gap),
    barSize: Math.floor(barSize),
    shouldScroll: false,
  };
}

// Minimum center-to-center spacing per label: the widest label ("May 15" ~40px,
// "Dec 15 '24" ~58px with a year tag) plus breathing room so ticks never crowd.
const X_AXIS_LABEL_WIDTH_PX = 60;
const X_AXIS_LABEL_WIDTH_WITH_YEAR_TAG_PX = 76;

/**
 * Calculate which X-axis tick values to render so labels never overlap.
 *
 * Returns evenly spaced periods that always include the first and last —
 * unlike recharts' stride `interval`, which drops the end label whenever
 * the stride doesn't land on it. The tick count is found by halving the
 * period count until it fits, so labels thin uniformly instead of leaving
 * a single hole when just one label too many would fit. Labels are assumed
 * wider when the range spans years, since day/month labels then carry a
 * year tag (omit `groupType` for charts that always group by day).
 */
export function calculateXAxisTicks(params: {
  periods: readonly string[];
  containerWidth: number;
  dateRange: CalendarDateRange;
  groupType?: TransactionGroupType;
  yAxisWidth?: number;
  padding?: number;
  margins?: number;
}): string[] {
  const { periods, containerWidth, dateRange, groupType, yAxisWidth = 80, padding = 40, margins = 10 } = params;

  const showsYearTag = groupType !== TransactionGroupType.YEAR && dateRange.start.year !== dateRange.end.year;
  const estimatedLabelWidth = showsYearTag ? X_AXIS_LABEL_WIDTH_WITH_YEAR_TAG_PX : X_AXIS_LABEL_WIDTH_PX;

  if (periods.length === 0) {
    return [];
  }

  const availableWidth = containerWidth - yAxisWidth - padding - margins;
  const numLabelsThatFit = Math.floor(availableWidth / estimatedLabelWidth);

  // Halve until it fits, keeping at least the two range endpoints
  let count = periods.length;
  while (count > numLabelsThatFit && count > 2) {
    count = Math.ceil(count / 2);
  }

  return selectEvenlySpacedItems(periods, Math.min(periods.length, Math.max(2, count)));
}

/**
 * Get the date range for a clicked bar period based on the group type.
 * For month grouping: returns the month start to month end
 * For year grouping: returns the year start to year end
 * The end date is clamped to today to prevent future dates.
 *
 * Returns undefined for day grouping since drilling down to a single day doesn't make sense.
 */
export function getDateRangeForPeriod(period: string, groupType: TransactionGroupType): CalendarDateRange | undefined {
  if (groupType === TransactionGroupType.DAY) {
    // For day grouping, don't change date range (clicking on a single day doesn't make sense)
    return undefined;
  }

  const now = today(getLocalTimeZone());
  const periodDate = parseDate(period);

  let start: CalendarDate;
  let end: CalendarDate;

  if (groupType === TransactionGroupType.MONTH) {
    // For month grouping: set to month start and month end
    start = startOfMonth(periodDate);
    end = endOfMonth(periodDate);
  } else {
    // For year grouping: set to year start and year end
    start = startOfMonth(periodDate.set({ month: 1 }));
    end = endOfMonth(periodDate.set({ month: 12 }));
  }

  // Ensure end date doesn't exceed today
  if (end.compare(now) > 0) {
    end = now;
  }

  return { start, end };
}
